import { useState, useEffect, useRef } from 'react'
import { useApp } from '../context/AppContext'
import { useAuth } from '../context/AuthContext'
import { OBJECTIVES, API_URL, sb } from '../utils/config'
import { Btn } from './ui'
// Served from public/ — same asset as /favicon.png. Stable URL, cached by
// the CDN, avoids re-bundling when we tweak the artwork.
const MERLIN_ICON = '/favicon.png'
import PricingModal from './PricingModal'

export default function NavBar() {
  const { page, setPage, profile, profiles, activeProfileIdx, switchProfile, deleteProfile, updateProfile, addProfile, setProfile, requestStrategyReset } = useApp()
  const { user, plan, monthlyUsage, maxCredits, isUnlimited, isAdmin, adminMode, setAdminMode, remainingCredits, signOut } = useAuth()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const [editingProfile, setEditingProfile] = useState(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editPosts, setEditPosts] = useState(3)
  const [editObj, setEditObj] = useState('balanced')
  const [showPricing, setShowPricing] = useState(false)
  const [showNewProfileMenu, setShowNewProfileMenu] = useState(false)
  const [importingFile, setImportingFile] = useState(false)
  const [importError, setImportError] = useState(null)
  const fileInputRef = useRef(null)
  const isDev = import.meta.env.VITE_APP_ENV === 'dev'
  // Quota par plan : free/basic/createur = 1 profil, pro = illimité (admin bypass)
  const profileQuota = isAdmin ? Infinity : (plan === 'pro' ? Infinity : 1)
  const canAddProfile = profiles.length < profileQuota
  const profileUpgradeTarget = profileQuota === 1 ? 'Pro' : null

  useEffect(() => {
    document.title = isDev ? 'Merlin [DEV]' : 'Merlin'
  }, [])

  const tabs = [
    { id: 'library', label: 'Ma stratégie' },
    { id: 'strategy', label: 'Mes scripts' },
    ...(isAdmin ? [{ id: 'client', label: 'Analyse client' }] : []),
    ...(isAdmin ? [
      { id: 'evaluate', label: 'Évaluer' },
      { id: 'explore', label: 'Explorer' },
      { id: 'admin', label: 'Admin' },
    ] : []),
  ]

  function startEdit(idx) {
    const p = profiles[idx]
    setEditingProfile(idx)
    setEditName(p.name || '')
    setEditDesc(p.description || '')
    setEditPosts(p.postsPerWeek || 3)
    setEditObj(p.objective || 'balanced')
  }
  function saveEdit() {
    if (editingProfile === null) return
    updateProfile(editingProfile, { name: editName, description: editDesc, postsPerWeek: editPosts, objective: editObj })
    setEditingProfile(null)
  }
  function handleNewProfile() {
    setShowProfileMenu(false)
    setShowNewProfileMenu(false)
    setPage('onboarding')
  }

  function exportQuestions() {
    const questions = `QUESTIONNAIRE MERLIN — Profil Client
========================================

Reponds a chaque question en quelques phrases. Plus tu es precis, meilleure sera ta strategie de contenu.

1. ACTIVITE / METIER
Decris ce que tu fais concretement. Quel est ton metier, ton business ?

2. SPECIFICITE
Qu'est-ce qui te differencie des autres dans ton domaine ? Quel est ton positionnement unique ?

3. AUDIENCE
A qui tu t'adresses ? Decris tes clients ou abonnes ideaux (age, situation, profil).

4. DOULEUR DE L'AUDIENCE
Quel est LE vrai probleme concret que tes clients vivent au quotidien ? Sois precis, pas vague. Exemple : "Ils bossent 60h par semaine et n'arrivent pas a decrocher leur premier client."

5. DESIR DE L'AUDIENCE
Que veulent-ils DEVENIR ? Pas juste un objectif, mais la transformation de vie qu'ils recherchent. Exemple : "Etre reconnus comme experts de reference et vivre de leur passion."

6. ENNEMI
Qu'est-ce qui BLOQUE ton audience ? Ca peut etre un systeme, une croyance, ou un frein interieur. Exemple : "Le syndrome de l'imposteur et la croyance qu'il faut etre deja celebre pour percer."

7. METHODE UNIQUE
Quelle est ton approche, ta methode, ton angle unique ? Pas un slogan, un vrai differenciant.

8. TON / ENERGIE
Quel est ton ton dominant ? (inspirant, provocateur, educatif, humoristique, authentique, expert, storyteller)

9. CONTRAINTES DE PRODUCTION
Qu'est-ce que tu es capable de produire ? Face camera ? Voix off ? Screen recording ? Texte seul ? Montage avance ?

10. SITUATION ACTUELLE
Ou en es-tu dans ton parcours de createur ?
- Je debute sur les reseaux (< 500 abonnes)
- J'ai deja une audience mais je veux grandir
- J'ai un produit/service pret a vendre
- J'ai deja un business qui tourne

11. PRODUIT / SERVICE
As-tu quelque chose a vendre ? Si oui, decris brievement.

========================================
Renvoie ce fichier rempli pour creer ton profil Merlin.`

    const blob = new Blob([questions], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'merlin-questionnaire-client.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function handleFileImport(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportingFile(true)
    setImportError(null)

    try {
      const text = await file.text()

      // Use Claude to parse the answers into a profile JSON
      const { data: sessionData } = await sb.auth.getSession()
      const token = sessionData?.session?.access_token
      const res = await fetch(`${API_URL}/api/proxy/anthropic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5-20250929',
          max_tokens: 1500,
          messages: [{
            role: 'user',
            content: `Voici les reponses d'un client au questionnaire Merlin. Extrais les informations et genere un JSON de profil.

REPONSES DU CLIENT :
${text}

Genere UNIQUEMENT un JSON valide (pas de markdown, pas de commentaire) avec cette structure exacte :
{
  "name": "Prenom ou nom du client (deduis-le des reponses, sinon 'Client')",
  "description": "resume de l'activite + specificite (2-3 phrases)",
  "audience": "description de l'audience cible",
  "audienceTarget": "version courte de l'audience (5-10 mots)",
  "audiencePain": "la douleur CONCRETE de l'audience",
  "audienceDesire": "ce que l'audience veut DEVENIR",
  "audienceEnemy": "ce qui BLOQUE l'audience",
  "creatorMethod": "l'approche unique du createur",
  "emotionalTone": "UN mot parmi: inspirant, provocateur, educatif, humoristique, authentique, expert, storyteller",
  "productionStyle": "UN ou PLUSIEURS parmi: face_cam, voix_off, screen_recording, texte_seul, montage_avance",
  "postsPerWeek": 5,
  "objective": "launch ou grow ou monetize ou scale",
  "hasProduct": true ou false
}

IMPORTANT : audiencePain, audienceDesire et audienceEnemy doivent etre PRECIS et DETAILLES, jamais vagues.`
          }],
        }),
      })

      if (!res.ok) throw new Error('Erreur lors de l\'analyse du fichier')
      const data = await res.json()
      const content = data.content?.[0]?.text || ''

      // Extract JSON from response
      const jsonMatch = content.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('Impossible d\'extraire le profil du fichier')

      const profileData = JSON.parse(jsonMatch[0])
      profileData.id = 'p_' + Date.now()
      if (!profileData.name) profileData.name = 'Client'

      addProfile(profileData)
      setShowProfileMenu(false)
      setShowNewProfileMenu(false)
      setPage('strategy')
    } catch (err) {
      console.error('[Import]', err)
      setImportError(err.message)
    }
    setImportingFile(false)
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <>
    <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b merlin-nav ${isDev ? 'merlin-nav--dev' : ''}`}>
      <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-12">
        <div className="flex items-center gap-3 cursor-pointer"
          onClick={() => {
            // Clicking Merlin always takes the user back to the formats feed.
            // 1. setPage('strategy') — navigate there if on another page.
            // 2. requestStrategyReset() — signal StrategyPage to reset its
            //    internal phase from 'review' back to 'select' so the user
            //    sees the formats list instead of landing on an old script.
            setPage('strategy')
            requestStrategyReset?.()
          }}>
          <img src={MERLIN_ICON} alt="Merlin" className="w-7 h-7 object-contain" style={{ filter: 'drop-shadow(0 0 8px rgba(254,130,4,0.25))' }} />
          <span className="text-sm font-bold tracking-tight merlin-nav-text">Merlin</span>
          {isDev && <span className="text-[9px] font-bold bg-orange text-white px-1.5 py-0.5 rounded">DEV</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* Credit gauge — desktop only */}
          <div className="hidden md:flex items-center gap-1.5 px-2 py-1">
            {isUnlimited ? (
              <span className="text-[9px] font-bold text-orange">{adminMode ? 'ADMIN ∞' : '∞'}</span>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-2 bg-border rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${remainingCredits() / maxCredits < 0.15 ? 'bg-red-500' : 'bg-orange'}`}
                    style={{ width: `${Math.max(2, Math.round(remainingCredits() / maxCredits * 100))}%` }}
                  />
                </div>
                <span className={`text-[9px] font-bold ${remainingCredits() / maxCredits < 0.15 ? 'text-red-400' : 'merlin-nav-text-muted'}`}>
                  {remainingCredits().toLocaleString()}
                </span>
                {plan !== 'pro' && (
                  <button onClick={() => setShowPricing(true)}
                    className="text-[8px] font-bold px-2 py-0.5 rounded-full transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', color: 'white', boxShadow: '0 1px 4px rgba(254,130,4,0.3)' }}>
                    Upgrade
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Admin mode toggle — desktop */}
          {isAdmin && (
            <label className="hidden md:flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-md hover:bg-surface-2 transition-all">
              <input
                type="checkbox"
                checked={adminMode}
                onChange={e => setAdminMode(e.target.checked)}
                className="w-3 h-3 accent-orange cursor-pointer"
              />
              <span className="text-xs font-bold text-orange">Admin</span>
            </label>
          )}

          {/* Bouton test onboarding (admin only) — reset les flags + reload */}
          {isAdmin && (
            <button
              onClick={() => {
                if (!confirm('Test du nouveau onboarding ?\n\nCa réinitialise les flags localStorage (showcase + scan) et te redirige sur la WelcomeShowcase. Ton profil DB reste intact.')) return
                try {
                  localStorage.removeItem('cg_showcase_seen')
                  localStorage.removeItem('cg_skip_scan')
                  localStorage.removeItem('cg_pending_ig_handle')
                  localStorage.removeItem('cg_scan_result')
                } catch {}
                const url = new URL(window.location.href)
                url.searchParams.set('test_onboarding', '1')
                window.location.href = url.toString()
              }}
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded-md bg-violet-100 text-violet-700 hover:bg-violet-200 text-[11px] font-bold transition-all"
              title="Reset les flags localStorage onboarding et recharge la page sur la WelcomeShowcase">
              🧪 Test onboarding
            </button>
          )}

          {profiles.length > 0 && (
            <div className="hidden md:block relative">
              <button onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-1.5 merlin-nav-profile px-2.5 py-1 text-xs font-medium transition-all">
                <span className="w-5 h-5 rounded-full bg-orange flex items-center justify-center text-white text-[9px] font-bold">
                  {(profile?.name || 'P')[0].toUpperCase()}
                </span>
                <span className="hidden sm:inline merlin-nav-text max-w-[100px] truncate">{profile?.name || 'Profil'}</span>
                <span className="text-[8px] merlin-nav-text-muted">▾</span>
              </button>

              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-[940]" onClick={() => setShowProfileMenu(false)} />
                <div className="fixed right-4 top-12 mt-1 w-72 merlin-dropdown shadow-lg z-[950] animate-fade" style={{ maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}>
                  <div className="p-3 border-b-2 border-border">
                    <p className="text-xs text-ink-faint font-mono mb-2">PROFILS</p>
                    <div className="space-y-1.5">
                      {profiles.map((p, i) => (
                        <div key={p.id || i} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${i === activeProfileIdx ? 'bg-orange-light sketch-orange' : 'sketch-light hover:border-ink'}`}>
                          <div className="flex-1 min-w-0" onClick={() => { switchProfile(i); setShowProfileMenu(false) }}>
                            <p className="text-xs font-medium text-ink truncate">{p.name || 'Profil'}</p>
                            <p className="text-xs text-ink-muted truncate">{p.description || ''}</p>
                            <div className="flex gap-1 mt-0.5">
                              <span className="text-[9px] text-ink-faint">{p.postsPerWeek || 3}/sem</span>
                              <span className="text-[9px] text-orange">{OBJECTIVES.find(o => o.id === p.objective)?.label || ''}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <button onClick={(e) => { e.stopPropagation(); startEdit(i) }} className="text-xs text-ink-muted hover:text-orange">✎</button>
                            {profiles.length > 1 && <button onClick={(e) => { e.stopPropagation(); deleteProfile(i) }} className="text-xs text-ink-muted hover:text-danger">✕</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {editingProfile !== null && (
                    <div className="p-3 border-b-2 border-border bg-surface-2 animate-fade">
                      <p className="text-xs text-ink-faint font-mono mb-2">MODIFIER</p>
                      <div className="space-y-2">
                        <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nom du profil"
                          className="w-full sketch-light bg-white px-2 py-1.5 text-xs text-ink" />
                        <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description (niche, activite...)"
                          rows={2} className="w-full sketch-light bg-white px-2 py-1.5 text-xs text-ink resize-none" />
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[9px] text-ink-faint">Posts/sem</label>
                            <input type="number" min="1" max="14" value={editPosts} onChange={e => setEditPosts(+e.target.value)}
                              className="w-full sketch-light bg-white px-2 py-1 text-xs text-ink" />
                          </div>
                          <div className="flex-1">
                            <label className="text-[9px] text-ink-faint">Objectif</label>
                            <select value={editObj} onChange={e => setEditObj(e.target.value)}
                              className="w-full sketch-light bg-white px-2 py-1 text-xs text-ink">
                              {OBJECTIVES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Btn size="sm" variant="orange" onClick={saveEdit}>Sauvegarder</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => setEditingProfile(null)}>Annuler</Btn>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-3 space-y-2">
                    {/* Hidden file input for import */}
                    <input ref={fileInputRef} type="file" accept=".txt,.md,.text" onChange={handleFileImport} className="hidden" />

                    {canAddProfile ? (
                      <>
                        {!showNewProfileMenu ? (
                          <button onClick={() => setShowNewProfileMenu(true)}
                            className="w-full sketch-dashed p-2 text-xs text-ink-muted hover:text-orange hover:border-orange transition-all text-center">
                            + Nouveau profil client
                          </button>
                        ) : (
                          <div className="space-y-1.5 animate-fade">
                            <p className="text-[9px] text-ink-faint font-mono mb-1">NOUVEAU PROFIL</p>
                            <button onClick={handleNewProfile}
                              className="w-full sketch-light bg-white p-2.5 text-left hover:border-orange transition-all rounded">
                              <p className="text-xs font-bold text-ink">Conversation avec Merlin</p>
                              <p className="text-[9px] text-ink-muted">Merlin pose les questions en direct</p>
                            </button>
                            <button onClick={() => { exportQuestions(); }}
                              className="w-full sketch-light bg-white p-2.5 text-left hover:border-orange transition-all rounded">
                              <p className="text-xs font-bold text-ink">Exporter le questionnaire</p>
                              <p className="text-[9px] text-ink-muted">Telecharge les questions a envoyer au client</p>
                            </button>
                            <button onClick={() => fileInputRef.current?.click()}
                              disabled={importingFile}
                              className="w-full sketch-light bg-white p-2.5 text-left hover:border-orange transition-all rounded disabled:opacity-50">
                              <p className="text-xs font-bold text-ink">{importingFile ? 'Analyse en cours...' : 'Importer les reponses'}</p>
                              <p className="text-[9px] text-ink-muted">Upload le fichier rempli par le client</p>
                            </button>
                            {importError && (
                              <p className="text-[9px] text-red-500 px-1">{importError}</p>
                            )}
                            <button onClick={() => setShowNewProfileMenu(false)}
                              className="w-full text-[9px] text-ink-faint hover:text-ink py-1 text-center">
                              Annuler
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="space-y-1.5">
                        <button onClick={() => { setShowPricing(true); setShowProfileMenu(false) }}
                          className="w-full p-2.5 text-center rounded-lg transition-all hover:scale-[1.01]"
                          style={{ background: 'rgba(254,130,4,0.06)', border: '1px solid rgba(254,130,4,0.15)' }}>
                          <div className="flex items-center justify-center gap-1.5 mb-0.5">
                            <span className="text-xs">🔒</span>
                            <p className="text-xs font-bold" style={{ color: '#fe8204' }}>
                              {profileQuota === Infinity ? 'Quota atteint' : `${profiles.length}/${profileQuota} profils utilisés`}
                            </p>
                          </div>
                          <p className="text-[9px] text-ink-muted">
                            {profileUpgradeTarget ? `Débloque des profils illimités avec ${profileUpgradeTarget}` : 'Limite atteinte sur ton plan'}
                          </p>
                        </button>
                        {/* Option : recommencer l'onboarding sur le profil actif.
                            Supprime le profil courant + relance OnboardingPage → repart de zéro.
                            Accessible pour tous les plans (utile pour pivoter). */}
                        <button onClick={() => {
                          if (!profile) return
                          if (!window.confirm(`Recommencer l'onboarding va SUPPRIMER le profil "${profile.name || 'actuel'}" et ses réglages (description, audience, produit...). Tes scripts et formats sauvegardés restent intacts. Continuer ?`)) return
                          deleteProfile(activeProfileIdx)
                          setShowProfileMenu(false)
                          setPage('onboarding')
                        }}
                          className="w-full p-2 text-center rounded-lg transition-all hover:bg-black/[0.02]"
                          style={{ border: '1px dashed rgba(0,0,0,0.1)' }}>
                          <p className="text-[10px] font-bold text-ink/70">🔄 Recommencer l'onboarding</p>
                          <p className="text-[9px] text-ink-muted mt-0.5">Repart à zéro sur ton profil actuel (utile pour pivoter)</p>
                        </button>
                      </div>
                    )}
                    {user && (
                      <div className="pt-2 border-t border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[9px] text-ink-faint">{user.email}</span>
                          <button onClick={() => { setShowPricing(true); setShowProfileMenu(false) }}
                            className="text-[9px] font-bold px-2 py-0.5 rounded-full transition-all hover:scale-105"
                            style={{ background: plan === 'free' ? 'linear-gradient(135deg, #fe8204, #f59e0b)' : 'rgba(124,58,237,0.08)', color: plan === 'free' ? 'white' : '#7c3aed' }}>
                            {plan === 'free' ? 'Upgrade' : plan.toUpperCase()}
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] text-ink-muted">
                            {isUnlimited
                              ? (adminMode ? 'Mode admin  -  credits illimites' : 'Credits illimites')
                              : `${remainingCredits().toLocaleString()} / ${maxCredits.toLocaleString()} credits restants`
                            }
                          </span>
                          <button onClick={signOut} className="text-[9px] text-red-400 hover:text-red-600">
                            Déconnexion
                          </button>
                        </div>
                        {isAdmin && (
                          <button onClick={() => {
                            try {
                              localStorage.removeItem('cg_library_tour_done')
                              localStorage.removeItem('cg_collect_tour_done')
                              localStorage.removeItem('cg_review_tour_done')
                              localStorage.setItem('cg_force_tuto', '1')
                            } catch {}
                            setShowProfileMenu(false)
                            window.location.reload()
                          }}
                            className="mt-2 w-full text-center text-[9px] text-ink-faint hover:text-orange transition-colors py-1">
                            Relancer les tutoriels
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
          )}

          {/* Desktop tabs */}
          <div className="hidden md:flex gap-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setPage(t.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 border ${page === t.id ? 'merlin-nav-tab-active' : 'merlin-nav-tab'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Mobile burger — opens full-screen nav */}
          <button
            onClick={() => setShowMobileNav(true)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-white/5"
            aria-label="Menu"
            style={{ color: 'rgba(255,255,255,0.9)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    {/* Mobile navigation panel */}
    {showMobileNav && (
      <>
        <div className="md:hidden fixed inset-0 z-[940] animate-fade"
          onClick={() => setShowMobileNav(false)}
          style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(6px)' }} />
        <div className="md:hidden fixed top-0 right-0 bottom-0 z-[950] w-72 max-w-[85vw] animate-slide-in p-5 flex flex-col"
          style={{
            background: 'linear-gradient(135deg, #1a1040 0%, #2d1b69 60%, #3b1f8e 100%)',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
          }}>
          <div className="flex items-center justify-between mb-6">
            <span className="text-base font-bold" style={{ color: 'rgba(255,255,255,0.95)' }}>Navigation</span>
            <button onClick={() => setShowMobileNav(false)}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              aria-label="Fermer">✕</button>
          </div>

          <nav className="flex flex-col gap-1">
            {tabs.map(t => (
              <button key={t.id}
                onClick={() => { setPage(t.id); setShowMobileNav(false) }}
                className="text-left px-4 py-3 rounded-xl transition-all text-sm font-semibold"
                style={page === t.id
                  ? { background: 'linear-gradient(135deg, rgba(254,130,4,0.18), rgba(245,158,11,0.12))', color: '#fe8204', border: '1px solid rgba(254,130,4,0.3)' }
                  : { color: 'rgba(255,255,255,0.75)', border: '1px solid transparent' }
                }>
                {t.label}
              </button>
            ))}
          </nav>

          {/* Credits summary in mobile menu */}
          {!isUnlimited && (
            <div className="mt-6 p-3.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>Crédits restants</span>
                <span className="text-sm font-bold" style={{ color: remainingCredits() / maxCredits < 0.15 ? '#ef4444' : '#fe8204' }}>
                  {remainingCredits().toLocaleString()}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.max(2, Math.round(remainingCredits() / maxCredits * 100))}%`,
                    background: remainingCredits() / maxCredits < 0.15 ? '#ef4444' : 'linear-gradient(90deg, #fe8204, #f59e0b)',
                  }} />
              </div>
              {plan !== 'pro' && (
                <button onClick={() => { setShowPricing(true); setShowMobileNav(false) }}
                  className="mt-3 w-full py-2 text-xs font-bold rounded-lg text-white transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)' }}>
                  Upgrader mon plan
                </button>
              )}
            </div>
          )}

          <div className="mt-auto pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {user?.email}
            </p>
            <button onClick={() => { signOut(); setShowMobileNav(false) }}
              className="mt-2 text-xs font-semibold transition-colors hover:opacity-80"
              style={{ color: '#ef4444' }}>
              Déconnexion
            </button>
          </div>
        </div>
      </>
    )}

    <PricingModal open={showPricing} onClose={() => setShowPricing(false)} />
    </>
  )
}
